// @flow
import * as React from 'react';

import AdminNewAutoMessageCompose from './AdminNewAutoMessageCompose';
import AdminNewAutoMessageCreate from './AdminNewAutoMessageCreate';
import AdminNewAutoMessageFormatCSV from './AdminNewAutoMessageFormatCSV';
import AdminNewAutoMessageForms from './AdminNewAutoMessageForms';
import AdminNewAutoMessageReview from './AdminNewAutoMessageReview';
import AdminNewAutoMessageSyncData from './AdminNewAutoMessageSyncData';
import Button from 'components/Button';
import ErrorReporter from 'app/components/ErrorReporter';
import LinkConstants from 'constants/LinkConstants';
import LoadingModal from 'components/LoadingModal';
import ToastActions from 'actions/ToastActions';
import WizardLayout from 'components/WizardLayout';

import type { GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages } from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageWithoutSends';
import type {
  AdminNewAutoMessageOrgs_organizations,
  SaveAutoMessageInput,
} from './queries/__generated__/AdminNewAutoMessageOrgs';
import type { AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials } from 'admin/components/AdminAutoMessageSftpCredentials/queries/__generated__/AdminAutoMessageSftpCredentials';

type Props = {
  onBack: () => void,
  onFieldChange: () => void,
  onResetFieldChange: () => void,
  orgs: ?(AdminNewAutoMessageOrgs_organizations[]),
  loading: boolean,
  hasCustomizedMessaging: boolean,
  save: (input: SaveAutoMessageInput) => Promise<*>,
  autoMessage: ?GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages,
  possibleSftpCredentials: ?(AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials[]),
  startingTab: ?*,
  viewingOrgUuid: ?string,
};

type State = {
  autoMessageFields: GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages,
};

class AdminNewAutoMessage extends React.Component<Props, State> {
  state = {
    autoMessageFields: {
      __typename: 'AutoMessage',
      sftpCredentialsUsername: undefined,
      messageName: '',
      organization: {
        __typename: 'Organization',
        uuid: '',
      },
      creatorOrganization: {
        __typename: 'Organization',
        uuid: '',
      },
      csvName: '',
      uuid: '',
      status: 'draft',
      includeParents: false,
      includeStudents: false,
      messageContent: undefined,
      sender: undefined,
      isActivatable: false,
      customMessageFields: [],
      isUrgent: false,
    },
  };

  componentDidMount() {
    const { autoMessage } = this.props;
    if (autoMessage) {
      this.pushAutoMessagePayloadToState(autoMessage);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.autoMessage && this.props.autoMessage) {
      this.pushAutoMessagePayloadToState(this.props.autoMessage);
    }
  }

  pushAutoMessagePayloadToState = (
    autoMessage: GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages
  ) => {
    this.setState({ autoMessageFields: autoMessage });
  };

  createAutoMessage = (messageName: ?string, orgUuid: ?string, creatorOrgUuid: ?string) => {
    if (!orgUuid || !messageName || !creatorOrgUuid) {
      console.warn({ orgUuid, messageName, creatorOrgUuid });
      console.error('AdminNewAutoMessage - tried to submit with missing required fields');
      return;
    }

    this.props
      .save({
        orgUuid,
        creatorOrgUuid,
        messageName,
      })
      .then(
        res => {
          const { autoMessage, error } = res.data.saveAutoMessage;
          if (error) {
            console.warn(error);
            console.error('AdminNewAutoMessage - saveAutoMessage failure');
            ToastActions.showGQLError(error);
            return;
          }
          this.pushAutoMessagePayloadToState(autoMessage);
        },
        err => {
          console.warn(err);
          console.error('AdminNewAutoMessage - saveAutoMessage failure');
          ToastActions.showGQLError(err);
        }
      );
  };

  saveAutoMessage = (fields: *, onSuccess: Function) => {
    const { autoMessageFields } = this.state;

    if (!autoMessageFields.uuid) {
      console.warn({ autoMessageFields });
      console.error('AdminNewAutoMessage - tried to edit a nonexistent auto message');
      ToastActions.showDefaultErrorToast();
      return;
    }

    /* eslint-disable no-unused-vars */
    const {
      organization,
      creatorOrganization,
      __typename,
      status,
      isActivatable,
      messageContent,
      sender,
      ...saveAutoMessageFields
    } = autoMessageFields;
    /* eslint-enable */

    const fieldsToSubmit = {
      ...saveAutoMessageFields,
      ...fields,
    };

    this.props.save(fieldsToSubmit).then(
      res => {
        const { autoMessage, error } = res.data.saveAutoMessage;
        if (error) {
          console.warn(error);
          console.error('AdminNewAutoMessage - saveAutoMessage failure');
          ToastActions.showGQLError(error);
          return;
        }
        this.pushAutoMessagePayloadToState(autoMessage);

        onSuccess();
      },
      err => {
        console.warn(err);
        console.error('AdminNewAutoMessage - saveAutoMessage failure');
        ToastActions.showGQLError(err);
      }
    );
  };

  render() {
    const {
      orgs,
      loading,
      possibleSftpCredentials,
      autoMessage,
      viewingOrgUuid,
      hasCustomizedMessaging,
    } = this.props;
    const { autoMessageFields } = this.state;
    const {
      uuid,
      messageName,
      organization,
      csvName,
      sftpCredentialsUsername,
      messageContent,
      sender,
      includeParents,
      includeStudents,
      status,
    } = autoMessageFields;

    if (loading) {
      return <LoadingModal />;
    }

    if (!orgs || orgs.length === 0) {
      return <ErrorReporter error="AdminNewAutoMessage - error loading user orgs" />;
    }

    if (!possibleSftpCredentials) {
      return <ErrorReporter error="AdminNewAutoMessage - error loading sftp creds" />;
    }

    const validOrgs = orgs.filter(org => org.permissions.canReceiveAutoMessages);
    const selectedOrg = validOrgs.find(org => org.uuid === organization.uuid);

    const tabs = [
      {
        tabKey: 'format-csv',
        name: 'Export CSV file',
        component: AdminNewAutoMessageFormatCSV,
        prevStep: undefined,
        nextStep: 'sync-data',
        isComplete: Boolean(csvName),
      },
      {
        tabKey: 'sync-data',
        name: 'Set up file transfer',
        component: AdminNewAutoMessageSyncData,
        prevStep: 'format-csv',
        nextStep: 'compose',
        isComplete: Boolean(sftpCredentialsUsername),
      },
      {
        tabKey: 'compose',
        name: 'Compose',
        component: AdminNewAutoMessageCompose,
        prevStep: 'sync-data',
        nextStep: 'review',
        isComplete: Boolean(
          messageName &&
            organization.uuid &&
            messageContent &&
            sender &&
            (includeParents || includeStudents)
        ),
      },
      {
        tabKey: 'review',
        name: 'Review',
        component: AdminNewAutoMessageReview,
        prevStep: 'compose',
        nextStep: undefined,
        isComplete: status === 'active',
      },
    ];

    if (uuid && selectedOrg) {
      return (
        <AdminNewAutoMessageForms
          orgs={validOrgs}
          onSave={this.saveAutoMessage}
          tabs={tabs}
          onBack={this.props.onBack}
          autoMessageFields={autoMessageFields}
          selectedOrg={selectedOrg}
          onFieldChange={this.props.onFieldChange}
          onResetFieldChange={this.props.onResetFieldChange}
          startingTab={this.props.startingTab}
          possibleSftpCredentials={possibleSftpCredentials}
          isEditingExistingAutoMessage={Boolean(autoMessage)}
          hasCustomizedMessaging={hasCustomizedMessaging}
        />
      );
    }

    return (
      <WizardLayout
        headerIcon="pencil-stroke-square"
        headerIconColor="shamrock"
        title="New auto message"
        onBack={this.props.onBack}
        nav={
          <Button
            href={LinkConstants.AUTO_MESSAGING_HELP}
            target="_blank"
            color="polar-bear"
            level="secondary"
            size="xsmall">
            Help
          </Button>
        }>
        <AdminNewAutoMessageCreate
          orgs={validOrgs}
          messageName={messageName}
          orgUuid={organization.uuid}
          viewingOrgUuid={viewingOrgUuid}
          onCreate={this.createAutoMessage}
        />
      </WizardLayout>
    );
  }
}

export default AdminNewAutoMessage;
